<template>
  <div class="layer-chart-preview">
    <!-- Hiển thị loading -->
    <div v-if="loading" class="text-center py-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2">Đang tải dữ liệu...</div>
    </div>

    <!-- Hiển thị khi chưa có cấu hình nào -->
    <v-alert v-else-if="chartConfigs.length === 0" type="info" text>
      Chưa có cấu hình biểu đồ nào
    </v-alert>

    <!-- Tabs chứa các biểu đồ -->
    <div v-else>
      <v-tabs v-model="activeTab" @change="handleTabClick" show-arrows>
        <v-tab v-for="chart in chartConfigs" :key="chart.id">
          {{ chart.name }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="mt-4">
        <v-tab-item v-for="chart in chartConfigs" :key="chart.id">
          <!-- Loading từng biểu đồ riêng lẻ -->
          <div v-if="loadingData[chart.id]" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
            <div class="mt-2">Đang truy vấn...</div>
          </div>

          <!-- Thông báo lỗi nếu query thất bại -->
          <v-alert v-else-if="errorMap[chart.id]" type="error" outlined class="mb-1">
            {{ errorMap[chart.id] }}
          </v-alert>

          <!-- Canvas biểu đồ -->
          <div v-else class="chart-container" style="position: relative; height: 250px; width: 100%">
            <ChartJSComponent v-if="chartDataMap[chart.id]" style="height: 100%" :type="chart.type"
              :labels="chartDataMap[chart.id].labels" :datasets="chartDataMap[chart.id].datasets"
              :options="getChartOptions(chart)" />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import ChartJSComponent from '@components/chart/ChartJSComponent.vue'
import { sdk } from '@service/http/axios'

const COLOR_PALETTE = [
  '#4A90E2', '#50E3C2', '#B8E986', '#F5A623', '#D0021B',
  '#BD10E0', '#9013FE', '#417505', '#7ED321', '#F8E71C'
]

export default {
  name: 'LayerChartPreview',
  components: { ChartJSComponent },
  props: {
    layerId: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: false,
      activeTab: 0,
      chartConfigs: [],
      chartDataMap: {},   // { chartId: { labels, datasets } }
      loadingData: {},    // { chartId: boolean }
      errorMap: {},       // { chartId: string }
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.loading = true
      // Xóa toàn bộ cache dữ liệu và cache lỗi cũ để luôn tải mới
      this.chartDataMap = {}
      this.errorMap = {}
      try {
        const { data: configs } = await sdk.request({
          url: `layer/${this.layerId}/chart-configs`,
          method: 'get'
        })
        this.chartConfigs = configs.list || configs.data || configs || []
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Lỗi tải danh sách biểu đồ' })
      } finally {
        this.loading = false
      }
    },

    async handleTabClick(index) {
      if (!this.chartConfigs[index]) return
      const chartId = this.chartConfigs[index].id
      // Nếu có lỗi cũ hoặc chưa có data, buộc phải tải lại từ Server
      if (this.errorMap[chartId] || !this.chartDataMap[chartId]) {
        this.$delete(this.errorMap, chartId)
        await this.loadChartData(chartId)
      }
    },

    previewChart(chartId) {
      const index = this.chartConfigs.findIndex(c => c.id === chartId)
      if (index !== -1) {
        this.activeTab = index
        // Xóa data cũ và lỗi cũ của biểu đồ này để ép tải lại dữ liệu mới nhất
        this.$delete(this.chartDataMap, chartId)
        this.$delete(this.errorMap, chartId)
        this.loadChartData(chartId)
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    async loadChartData(chartId) {
      this.$set(this.loadingData, chartId, true)
      this.$delete(this.errorMap, chartId)

      try {
        const chart = this.chartConfigs.find(c => c.id === chartId)
        const { data } = await sdk.request({
          url: `layer/${this.layerId}/chart-configs/${chartId}/data`,
          method: 'get'
        })

        // Xử lý tối ưu UI (Cắt top và gom nhóm "Khác")
        const processedData = this.processDataForChart(data, chart.type)
        const labelsCount = processedData.labels.length

        this.$set(this.chartDataMap, chartId, {
          labels: processedData.labels,
          datasets: [{
            label: processedData.datasets[0].label,
            data: processedData.datasets[0].data,
            fullLabels: processedData.fullLabels,
            backgroundColor: this.generateColors(labelsCount, processedData.hasOther),
            borderColor: this.generateColors(labelsCount, processedData.hasOther),
            borderWidth: 1,
            fill: false,
          }],
        })
      } catch (e) {
        const msg = e.response?.data?.message || e.response?.data?.error || 'Lỗi truy vấn dữ liệu'
        this.$set(this.errorMap, chartId, msg)
      } finally {
        this.$set(this.loadingData, chartId, false)
      }
    },

    getChartOptions(chart) {
      const isPieType = chart.type === 'pie' || chart.type === 'doughnut'
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { 
          display: isPieType, 
          position: 'right',
          labels: { boxWidth: 12 }
        },
        title: { display: true, text: chart.name },
        scales: isPieType ? {} : {
          yAxes: [{ ticks: { beginAtZero: true } }],
        },
        tooltips: {
          callbacks: {
            title: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem[0].datasetIndex];
              if (dataset.fullLabels) {
                return dataset.fullLabels[tooltipItem[0].index];
              }
              return data.labels[tooltipItem[0].index];
            }
          }
        }
      }
    },

    generateColors(count, hasOther = false) {
      return Array.from({ length: count }, (_, i) => {
        // Gán màu xám trung tính cho nhóm "Khác..."
        if (hasOther && i === count - 1) {
          return '#9E9E9E';
        }
        return COLOR_PALETTE[i % COLOR_PALETTE.length];
      })
    },

    /**
     * Xử lý tối ưu hiển thị
     */
    processDataForChart(rawData, chartType) {
      const isPieType = chartType === 'pie' || chartType === 'doughnut';
      const MAX_LABELS = isPieType ? 6 : 10;
      let labels = rawData.labels || [];
      let data = rawData.datasets[0].data || [];
      
      let newLabels = labels.slice(0, MAX_LABELS);
      let newData = data.slice(0, MAX_LABELS);
      let fullLabels = labels.slice(0, MAX_LABELS);

      let hasOther = false;
      if (labels.length > MAX_LABELS) {
        let othersSum = data.slice(MAX_LABELS).reduce((a, b) => Number(a) + Number(b), 0);
        newLabels.push('Khác...');
        fullLabels.push('Khác...');
        newData.push(othersSum);
        hasOther = true;
      }

      // Cắt ngắn nhãn để hiển thị đẹp trên Trục X và Legend
      let displayLabels = newLabels.map(lbl => {
          if (typeof lbl === 'string' && lbl.length > 25) {
              return lbl.substring(0, 25) + '...';
          }
          return lbl;
      });

      return {
        labels: displayLabels,
        fullLabels: fullLabels,
        hasOther: hasOther,
        datasets: [{
          ...rawData.datasets[0],
          data: newData
        }]
      };
    },
  },
}
</script>
